import { Box } from '@chronosphereio/chrono-ui';
import { BarSeriesOption, BarChart as EChartsBarChart } from 'echarts/charts';
import { GridComponent, GridComponentOption, TooltipComponent, TooltipComponentOption } from 'echarts/components';
import { ComposeOption, use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useMemo } from 'react';
import { ContentDimensions } from '../../core';
import { useChartsTheme } from '../context/ChartsProvider';
import { EChart } from '../EChart';

type BarChartOption = ComposeOption<BarSeriesOption | TooltipComponentOption | GridComponentOption>;

// eslint-disable-next-line react-hooks/rules-of-hooks
use([EChartsBarChart, GridComponent, TooltipComponent, CanvasRenderer]);

export interface BarPieChartData {
  /**
   * The label only appears in the tooltip
   */
  label: string;
  value: number | null;
  /**
   * Overrides the default color for the section applied by the theme
   */
  color?: string;
}

export interface BarPieChartBaseProps extends ContentDimensions {
  /**
   * The name for the chart appears as the title of the tooltip
   */
  name: string;
  data: BarPieChartData[] | null;
  /**
   * If a total is provided, the length of the bar will be based on this value
   * with negative space added if the items add up to less than the total
   */
  total: number;
}

/**
 * BarPieChart is a simple horizontal stacked bar chart used the same way you would a pie chart. This is helpful
 * when you want to display the relative values that make up a whole without emphasizing the absolute values.
 *
 * A common use case is to have a visual representation of two different allocations, in this case you would want
 * to use two bars, each with the same names and colors but different values.
 */
export function BarPieChartBase(props: BarPieChartBaseProps) {
  const { width, height, data, name, total } = props;
  const chartsTheme = useChartsTheme();

  const option = useMemo(() => {
    if (data == null || !data.length) return chartsTheme.noDataOption;

    const series: BarSeriesOption[] = data.map((item) => {
      return {
        name: item.label,
        type: 'bar',
        stack: 'total',
        emphasis: {
          focus: 'series',
        },
        showBackground: true,
        data: [
          {
            value: item.value !== null ? item.value : undefined,
            itemStyle: {
              color: item.color,
            },
          },
        ],
      };
    });

    const option: BarChartOption = {
      grid: {
        left: 0,
        right: 0,
        bottom: 0,
        containLabel: false,
      },
      tooltip: {
        trigger: 'axis',
      },
      animation: false,
      title: {
        show: false,
      },
      xAxis: {
        show: false,
        type: 'value',
        min: 0,
        max: total,
      },
      yAxis: {
        show: false,
        type: 'category',
        data: [name],
      },
      series,
    };

    return option;
  }, [data, chartsTheme.noDataOption, total, name]);

  return (
    <Box sx={{ width: width, height: height }}>
      <EChart width={width} height={height} option={option} theme={chartsTheme.echartsTheme} />
    </Box>
  );
}
